import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { clientsApi, settingsApi } from '../../api/endpoints.js';
import type { ClientSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  EmptyState,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  StatusBadge,
  SwitchField,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * The client register.
 *
 * DENSE, NOT CRAMPED (UX pass). One row per client with what the desk looks
 * for: the name — a link to the file — with the client ID and type beneath
 * it, how to reach them, the city, and the status. Fewer, fuller columns
 * keep the table readable on a tablet and scroll sideways on a phone rather
 * than squeezing every value into one word per line.
 *
 * A laboratory with no clients yet sees what to do next, not an empty grid.
 */
export function ClientsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [includeSuspended, setIncludeSuspended] = useState(true);
  const [page, setPage] = useState(1);

  const canCreate = can(PERMISSIONS.CLIENTS_CREATE);
  const canUpdate = can(PERMISSIONS.CLIENTS_UPDATE);

  // The laboratory's own time zone, so dates read in local terms even though
  // they are stored in UTC.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
    retry: false,
  });

  const clients = useQuery({
    queryKey: ['clients', { page, search, includeSuspended }],
    queryFn: () => clientsApi.list({ page, search, includeSuspended }),
  });

  const timezone = settings.data?.timezone;
  const rows = clients.data?.data ?? [];
  const isEmptyRegister =
    !clients.isLoading && !clients.error && rows.length === 0 && !search.trim();

  const columns: readonly DataColumn<ClientSummary>[] = [
    {
      key: 'name',
      header: t('clients.name'),
      render: (client) => (
        // The name opens the file: the row's way in (D278).
        <div className="min-w-48">
          <Link
            to={`/clients/${client.id}`}
            className="font-semibold text-default-900 underline-offset-2 hover:text-primary-700 hover:underline"
            dir="auto"
          >
            {client.name || t('common.notRecorded')}
          </Link>
          <p className="text-caption text-default-600">
            {/* Read off paper and typed into search boxes: always LTR. */}
            <span dir="ltr" className="measure">
              {client.clientNumber}
            </span>
            {' · '}
            {t(`clients.type.${client.clientType}`)}
          </p>
        </div>
      ),
    },
    {
      key: 'contact',
      header: t('clients.contactDetails'),
      render: (client) =>
        client.contactEmail || client.contactPhone ? (
          <div className="text-caption text-default-700">
            {client.contactEmail ? <p dir="ltr">{client.contactEmail}</p> : null}
            {client.contactPhone ? <p dir="ltr">{client.contactPhone}</p> : null}
          </div>
        ) : (
          // Never a blank cell: an empty field is a fact about the record,
          // not a rendering accident.
          <span className="text-caption text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'city',
      header: t('clients.city'),
      render: (client) =>
        client.city ? (
          <span className="text-default-700" dir="auto">
            {client.city}
          </span>
        ) : (
          <span className="text-caption text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (client) => (
        <StatusBadge
          size="sm"
          label={t(`clients.status.${client.status}`)}
          tone={client.status === 'active' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      render: (client) => (
        <span className="whitespace-nowrap text-caption text-default-600">
          {formatDate(client.createdAt, {
            locale,
            ...(timezone ? { timezone } : {}),
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (client) => (
        <div className="flex justify-end gap-inline">
          {canUpdate ? (
            <ActionButton
              priority="tertiary"
              size="sm"
              onPress={() => navigate(`/clients/${client.id}/edit`)}
            >
              {t('common.edit')}
            </ActionButton>
          ) : null}
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => navigate(`/clients/${client.id}`)}
          >
            {t('common.open')}
          </ActionButton>
        </div>
      ),
    },
  ];

  const createButton = canCreate ? (
    <ActionButton priority="primary" onPress={() => navigate('/clients/new')}>
      {t('clients.create')}
    </ActionButton>
  ) : null;

  return (
    <>
      <PageHeader
        title={t('clients.title')}
        subtitle={t('clients.subtitle')}
        breadcrumbs={[{ label: t('nav.clients') }]}
        actions={createButton}
      />

      {isEmptyRegister ? (
        <EmptyState
          title={t('clients.noneYet')}
          description={t('clients.noneYetHint')}
          action={createButton}
        />
      ) : (
        <Panel>
          <FilterBar>
            <div className="w-full min-w-0 sm:w-80">
              <SearchField
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
                placeholder={t('clients.searchPlaceholder')}
              />
            </div>
            <div className="pb-1">
              <SwitchField
                label={t('clients.includeSuspended')}
                value={includeSuspended}
                onChange={(value) => {
                  setIncludeSuspended(value);
                  setPage(1);
                }}
              />
            </div>
          </FilterBar>

          <DataTable
            label={t('clients.title')}
            columns={columns}
            rows={rows}
            rowKey={(client) => client.id}
            isLoading={clients.isLoading}
            error={clients.error}
            onRetry={() => void clients.refetch()}
            emptyContent={search ? t('clients.noneFound') : t('clients.noneYet')}
            page={page}
            totalPages={clients.data?.meta.totalPages ?? 1}
            onPageChange={setPage}
          />
        </Panel>
      )}
    </>
  );
}
